import React, { useCallback, useEffect, useState } from 'react';
import Modal from '@atlaskit/modal-dialog';
import styled from 'styled-components';
import Button from 'kingpin/atoms/Button';
import Typography from 'kingpin/atoms/Typography';
import CopyInput from 'kingpin/atoms/CopyText';
import KingpinIcon from 'kingpin/atoms/Icon';

import Row from '../Common/Row';
import Inputs from '../Inputs';
import { CONFIRMATION_REQUIRED_INPUT_TEST_ID } from './constants';
import Icon from './Icon';

const MainDiv = styled.div`
  background-color: var(--color-bg-primary);
`;
const BodyDiv = styled.div`
  padding: 40px 32px 24px 32px;
  border-bottom: solid 1px var(--color-bg-input);

  display: flex;
  flex-direction: column;
  align-items: center;
`;
const BottomBarDiv = styled.div`
  padding: 16px 32px;
`;

const InputConfirmationModal = ({
  requiredInput,
  title,
  body,
  isOpen,
  close,
  onConfirmed,
  buttonTestId,
  inputTestId,
}: {
  requiredInput: string;
  title: string;
  body: React.ReactNode;
  isOpen: boolean;
  close: () => void;
  onConfirmed: () => void;
  buttonTestId?: string;
  inputTestId?: string;
}) => {
  const [text, setText] = useState<string>('');
  const onTextChanged = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setText(event.target.value);
    },
    [],
  );

  const onConfirmClicked = useCallback(() => {
    if (text !== requiredInput) {
      return;
    }

    onConfirmed();
  }, [requiredInput, onConfirmed, text]);

  useEffect(() => {
    if (!isOpen) {
      setText('');
    }
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  return (
    <Modal
      onClose={close}
      shouldScrollInViewport={false}
      autoFocus={false}
      width={448}
    >
      <MainDiv>
        <BodyDiv>
          <Icon type={'danger'} />
          <div style={{ marginBottom: 12 }}>
            <Typography.Header type="H4">{title}</Typography.Header>
          </div>
          <div style={{ marginBottom: 24, width: '100%' }}>
            <Typography.Body type="Body 12">{body}</Typography.Body>
          </div>
          <div style={{ marginBottom: 8, width: '100%' }}>
            <Typography.Body type="Body 12">
              To confirm, type the text below:
            </Typography.Body>
          </div>
          <div
            style={{ width: '100%' }}
            data-testid={CONFIRMATION_REQUIRED_INPUT_TEST_ID}
          >
            <CopyInput text={requiredInput} />
          </div>
          <div style={{ display: 'flex', margin: '4px 0' }}>
            <KingpinIcon icon="arrow-down" color="var(--color-grey-4)" />
          </div>
          <div style={{ marginBottom: 24, width: '100%' }}>
            <Inputs.TextInput
              data-testid={inputTestId || 'confirmation-input'}
              value={text}
              onChange={onTextChanged}
              placeholder={requiredInput}
            />
          </div>
        </BodyDiv>
        <BottomBarDiv>
          <Row spaceBetween centerAlign>
            <Button
              type="Tertiary"
              size="Small"
              onClick={close}
              label="Cancel"
            />
            <Button
              testId={buttonTestId || 'confirmation-button'}
              onClick={onConfirmClicked}
              label="Continue"
              isDisabled={text !== requiredInput}
              type="Primary"
              size="Small"
            />
          </Row>
        </BottomBarDiv>
      </MainDiv>
    </Modal>
  );
};

export default InputConfirmationModal;
